/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { priceType } from '@/context/pricetype';
import { stringify } from 'querystring';
import BaseServiceApi from './baseapi';

export interface UsrContactType {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  saveFlag: boolean;
}

export interface OrderType {
  priceId: string;
  productId: number;
  paymentType: string;
  paymentMethodId: string;
  productStripeId: number;
  couponCode: string;
  saveFlag: boolean;
  userContact: UsrContactType;
  source?: string;
  medium?: string;
}

export interface OrderResultType {
  purchaseId: number;
  paymentId: string;
  paymentType: string;
  price: string;
  currency: string;
  paylaod: string;
  subscriptionId: string;
  clientSecret: string;
  chargeNow: boolean;
}

export interface QueryCouponType {
  couponCode: string;
  priceId: string;
  productCode: string;
  productId: number;
}

export interface QueryCouponApiType {
  amount: string;
  isFixed: boolean;
}

export interface PreviewOrderType {
  lang: string;
  productStripeId: number;
  coupon: string;
  paymentMethodId?: string;
}

export interface PreviewOrderApiType {
  plan: {
    productStripeId: string;
    productCode: string;
    productName: string;
    productDesc: string;
    stdPrice: string;
    currency: string;
    intervalCount: number;
  };
  trial: number;
  deduct: {
    currency: string;
    deductOrders: any[];
    description: string;
    totalCredit: string;
  } | null;
  discount: {
    coupon: string;
    desc: string;
    valid: boolean;
    amount: string;
    currency: string;
    isFixed: boolean;
  } | null;
  totalPrice: number;
  desc: string;
  applying_coupon_desc?: string;
  fixCurrency: string | null;
}

// 在重新订阅的情况下api 结果如下
export interface PreviewOrderApiAnotherType {
  reactive: {
    cardId: number;
    orderId: number;
    fixCurrency: string;
  };
  reSubscription: boolean;
  purchase: PreviewOrderApiType | null;
}

export interface CardDataType {
  brand: string;
  fundingType: string;
  last4: string;
  fingerprint: string;
  expMonth: number;
  expYear: number;
  paymentMethodId: string;
  cardId: number;
}

export interface PlansType {
  productStripeId: number;
  productCode: string;
  productName: string;
  productDesc: string;
  origPrice: string;
  currency: string;
  interval: string;
  intervalCount: number;
  startDate: string;
  expireDate: string;
  isSub: boolean;
  subPlatform: string;
  canUpgrade: boolean;
  canConvert: boolean;
  canReactivate: boolean;
  freqList: freqType[];
  nextBillingDate: string;
  orderId: number;
  last4: string;
  stdPrice: string;
  brand: string;
  nextIntervalCount: number;
  recommended: RecommendedType;
  daysRemaining: number;
}

export interface freqType {
  currency: string;
  desc: string;
  intervalCount: number;
  price: string;
  savingPct: string;
}

export interface RecommendedType {
  productStripeId: number;
  productCode: string;
  productName: string;
  productDesc: string;
  origPrice: string;
  currentPrice: string;
  currency: string;
  interval: string;
  intervalCount: number;
}

export interface StripeDoneType {
  paymentIntentId?: string;
  setupIntentId?: string;
  productId: number;
  purchaseId: number;
  couponCode: string;
}

interface ConfirmChangeCardDateType {
  paymentMethodId: string;
  purchaseId: number;
  cardId: number;
  updateAll: false;
}

export interface StripeErrorType {
  code: string;
  decline_code: string;
  message?: string;
}

export interface PromotionActivityType {
  isAvailable: boolean;
  expireDate: string;
  isFixed: boolean;
  trialDays: number;
  percent: number;
  amount: {
    CNY: number;
    HKD: number;
    USD: number;
    SGD: number;
  };
}

export function createOrder(data: OrderType) {
  return BaseServiceApi.post<{ data: OrderResultType; error: StripeErrorType }>('/api/v2/ch/pc/pc', data);
}

export function previewOrder(data: PreviewOrderType) {
  return BaseServiceApi.post<{ data: PreviewOrderApiAnotherType }>('/api/v2/ch/pc/po', data);
}

export function queryCoupon(data: QueryCouponType) {
  return BaseServiceApi.get<QueryCouponApiType>('/api/v2/ch/pc/ac', data);
}

export function getCardList() {
  return BaseServiceApi.get<{ cards: CardDataType[] }>('/api/v2/ch/pc/c', { pageSize: 10, pageNumber: 0 });
}

export function getPlans(lang: string, referral = '') {
  if (referral) {
    return BaseServiceApi.get<{ data: { myPlans: PlansType[] } }>('/api/v2/ch/pc/up', {
      lang,
      referral,
    });
  }
  return BaseServiceApi.get<{ data: { myPlans: PlansType[] } }>('/api/v2/ch/pc/up', {
    lang,
  });
}

export function stopSubscription(orderId: number) {
  return BaseServiceApi.post<{ data: { success: boolean } }>('/api/v2/ch/pc/cs', { orderId });
}

export function reActive(orderId: number) {
  return BaseServiceApi.post<{ data: { success: boolean } }>('/api/v2/ch/pc/rs', { orderId });
}

export function getTrial(data: { priceId: string; paymentMethodId: string; productCode: string }) {
  return BaseServiceApi.get<{ days: number }>('/api/v2/ch/pc/tp', data);
}

export function stripeDone(data: StripeDoneType) {
  return BaseServiceApi.post<{ success: boolean; error: StripeErrorType }>('/api/v2/ch/pc/std', data);
}

// 银行卡合法性校验， 防止银行卡无效也能绑定
export function checkCard(data: { paymentMethodId: string; currency: string; save: boolean }) {
  return BaseServiceApi.get<{
    clientSecret: string;
    cardId: number;
    verifyed: boolean;
    error: StripeErrorType;
  }>('/api/v2/ch/pc/gps', data);
}

// stripe确认可以切换卡
// 确认切换银行卡意向的api
export function confirmChangeCard(data: ConfirmChangeCardDateType) {
  return BaseServiceApi.post<{}>('/api/v2/ch/pc/usp', data);
}

// 切换订阅周期的api
export function reviceSubscriptFreq(data: { orderId: number; targetFreq: number }) {
  return BaseServiceApi.post<{ success: boolean }>('/api/v2/ch/pc/bf', data);
}

// 将银行卡设置为false
export function setCardFalse(data: { cardId: number; saveFlag: false }) {
  return BaseServiceApi.post('/api/v2/ch/pc/uap', data);
}

// 查询活动api
export function queryPromotionActivity(data: { couponCode: string }) {
  return BaseServiceApi.get<PromotionActivityType>('/api/v2/ch/pc/qc', data);
}
